import { Fragment, h } from '@atomify/jsx';
import { MapDataItem, MapOptions, MapTheme } from '@bpd-library/types';
import { calculateCenter, functionIsTrue } from '@bpd-library/utilities';
import mapboxgl from 'mapbox-gl';

const variants: string[] = [];
const debugMarkers: mapboxgl.Popup[] = [];

const addData = async (
    map: mapboxgl.Map,
    data: MapDataItem[],
    theme: MapTheme,
    options: MapOptions,
) => {
    const features: { [variant: string]: GeoJSON.Feature[] } = {};

    /*
        Create empty arrays for previously set variants,
        this will make sure old variants will be removed.
    */
    variants.forEach((variant) => (features[variant] = []));

    /*
        Construct GeoJSON Features
    */
    data.forEach((item) => {
        const { variant } = item;

        if (variants.indexOf(variant) < 0) {
            variants.push(variant);
            features[variant] = [];
        }

        if (isPointerVariant(variant, theme)) {
            features[variant].push({
                type: 'Feature',
                geometry: {
                    coordinates: calculateCenter(item.coordinates),
                    type: 'Point',
                },
                properties: {
                    ...item,
                },
            });
        } else {
            item.coordinates.forEach((coordinate) => {
                features[variant].push({
                    type: 'Feature',
                    geometry: {
                        coordinates: [coordinate],
                        type: 'Polygon',
                    },
                    properties: {
                        ...item,
                    },
                });
            });
        }
    });

    /*
        Update layer with features
    */
    await functionIsTrue(() => map.isStyleLoaded() && map.isSourceLoaded);

    Object.keys(features).forEach((variant) => {
        const variantFeatures = features[variant];

        const source = map.getSource(variant) as mapboxgl.GeoJSONSource | undefined;

        if (source) {
            source.setData({
                type: 'FeatureCollection',
                features: variantFeatures,
            });
        }
    });

    if (options.debugMode) {
        addDebugMarkers(map, data, options);
    }
};

/**
 * @description Checks if the theme variant is a pointer theme
 * @param {string} variant
 * @param {MapTheme} theme
 * @return {*}
 */
const isPointerVariant = (variant: string, theme: MapTheme) => {
    return !!theme.pointer?.settings.find((pointerTheme) => pointerTheme.variant === variant);
};

/**
 * @description Adds debug markers to the map, based on the mapdata
 * @param {mapboxgl.Map} map
 * @param {MapDataItem[]} mapData
 * @param {MapOptions} options
 */
const addDebugMarkers = (map: mapboxgl.Map, mapData: MapDataItem[], options: MapOptions) => {
    const debugProperties = options.debugProperties || ['id'];

    // Remove all markers
    debugMarkers.forEach((marker) => marker.remove());

    mapData.forEach((mapDataItem) => {
        const center = calculateCenter(mapDataItem.coordinates);

        const marker = new mapboxgl.Popup({
            closeOnMove: false,
            closeOnClick: false,
            closeButton: false,
            focusAfterOpen: false,
        })
            .setDOMContent(
                <div
                    style={{
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        color: '#fff',
                        fontSize: '10px',
                    }}>
                    {debugProperties
                        .map((property) => mapDataItem[property])
                        .filter(Boolean)
                        .map((value, i) => (
                            <Fragment>
                                {i > 0 && <br />}
                                {value}
                            </Fragment>
                        ))}
                </div>,
            )
            .setLngLat(center)
            .addTo(map);

        debugMarkers.push(marker);
    });
};

export default addData;
